import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { ANALYTICS_COLOR_PALETTE } from '../../common/utils/colorUtils';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  margin: 0 auto;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  width: 100%;
  margin-top: 20px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const ColorIndicator = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
`;

const LocaleName = styled.span`
  font-weight: 600;
  color: #333;
  font-size: 0.95rem;
`;

const LocaleStats = styled.span`
  font-size: 0.85rem;
  color: #666;
  margin-top: 2px;
`;

// Enhanced color palette with better contrast - now using centralized colors
const COLORS = ANALYTICS_COLOR_PALETTE;

export const ChartJsView = ({ data }) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    return {
      labels: data.map(item => item.label),
      datasets: [
        {
          data: data.map(item => item.count),
          backgroundColor: data.map((_, index) => COLORS[index % COLORS.length]),
          borderColor: data.map((_, index) => COLORS[index % COLORS.length]),
          borderWidth: 2,
          hoverBorderWidth: 3,
          hoverOffset: 8,
        },
      ],
    };
  }, [data]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll use custom legend below
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const percentage = ((context.parsed / context.dataset.data.reduce((a, b) => a + b, 0)) * 100).toFixed(1);
            return `${context.label}: ${context.parsed} sessions (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1000,
    },
    cutout: '60%', // Makes it a doughnut chart
  }), []);

  if (!chartData) {
    return (
      <ChartContainer>
        <p>No language distribution data available</p>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <div style={{ width: '300px', height: '300px' }}>
        <Doughnut data={chartData} options={chartOptions} />
      </div>
      
      <StatsGrid>
        {data.map((locale, index) => (
          <StatItem key={`${locale.locale || 'unknown'}-${index}`}>
            <ColorIndicator color={COLORS[index % COLORS.length]} />
            <StatText>
              <LocaleName>{locale.label}</LocaleName>
              <LocaleStats>
                {locale.count} sessions ({locale.percentage}%)
              </LocaleStats>
            </StatText>
          </StatItem>
        ))}
      </StatsGrid>
    </ChartContainer>
  );
};

ChartJsView.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      locale: PropTypes.string,
      label: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      percentage: PropTypes.number.isRequired,
    })
  )
};

ChartJsView.defaultProps = {
  data: []
};

ChartJsView.displayName = 'ChartJsView';
